import React, { useEffect, useState, useCallback } from 'react';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Target } from 'lucide-react';
import api, { fmtIDR, currentMonth } from '@/lib/api';
import { toast } from 'sonner';

export default function OwnerKpi() {
  const [salesmen, setSalesmen] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [targets, setTargets] = useState([]);
  const [salesmanId, setSalesmanId] = useState('');
  const [amount, setAmount] = useState('');

  const load = useCallback(async () => {
    try {
      const [su, tg] = await Promise.all([
        api.get('/users?role=salesman'),
        api.get(`/kpi/targets?month=${month}`),
      ]);
      setSalesmen(su.data);
      setTargets(tg.data);
    } catch (e) { console.error('[OwnerKpi] gagal load:', e); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!salesmanId || !amount) return toast.error('Isi salesman dan target');
    try {
      await api.post('/kpi/target', { salesman_id: salesmanId, month, target_amount: Number(amount) });
      toast.success('Target tersimpan');
      setAmount(''); setSalesmanId('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
  };

  return (
    <AppShell nav={ownerNav} title="KPI Target" showBell>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Target className="h-5 w-5" /></div>
              <div>
                <div className="font-heading font-semibold">Set Target Bulanan</div>
                <div className="text-xs text-muted-foreground">Owner only</div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Bulan (YYYY-MM)</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="kpi-month" />
            </div>
            <div className="space-y-1.5">
              <Label>Salesman</Label>
              <Select value={salesmanId} onValueChange={setSalesmanId}>
                <SelectTrigger data-testid="kpi-salesman-select"><SelectValue placeholder="Pilih salesman" /></SelectTrigger>
                <SelectContent>
                  {salesmen.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Target Omset (Rp)</Label>
              <Input type="number" min="0" step="100000" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="kpi-amount" />
            </div>
            <Button onClick={save} className="w-full" data-testid="kpi-save-button">Simpan Target</Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bulan</TableHead>
                  <TableHead>Salesman</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {targets.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-8">Belum ada target untuk bulan ini</TableCell></TableRow>
                ) : targets.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.month}</TableCell>
                    <TableCell>{t.salesman_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtIDR(t.target_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
